import { useRouter } from 'expo-router';
import { useEffect, useState } from 'react';
import { Animated, Modal, SafeAreaView, StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import { useAuth } from '../../../context/AuthContext';
import { useResponsive } from '../../../hooks/useResponsive';
import { COLORS } from '../../../styles/colors';
import env from '../../../utils/environment';
import { fontFamilies } from '../../../utils/fontLoader';

const MenuIcon = () => (
  <View style={{ width: 28, height: 28, justifyContent: 'center' }}>
    <View style={{ height: 2, backgroundColor: COLORS.white, marginBottom: 6 }} />
    <View style={{ height: 2, backgroundColor: COLORS.white, marginBottom: 6 }} />
    <View style={{ height: 2, backgroundColor: COLORS.white }} />
  </View>
);

const CloseIcon = () => (
  <View style={{ width: 28, height: 28, justifyContent: 'center', alignItems: 'center' }}>
    <View style={{
      position: 'absolute',
      width: 28,
      height: 2,
      backgroundColor: COLORS.textPrimary,
      transform: [{ rotate: '45deg' }]
    }} />
    <View style={{
      position: 'absolute',
      width: 28,
      height: 2,
      backgroundColor: COLORS.textPrimary,
      transform: [{ rotate: '-45deg' }]
    }} />
  </View>
);

const Header = ({ content, onCtaPress, onNavPress }) => {
  const { isMobile } = useResponsive();
  const { user } = useAuth();
  const router = useRouter();
  const [isMenuVisible, setIsMenuVisible] = useState(false);
  const [fadeAnim] = useState(new Animated.Value(1));

  useEffect(() => {
    if (user !== null) {
      Animated.sequence([
        Animated.timing(fadeAnim, {
          toValue: 0,
          duration: 100,
          useNativeDriver: true,
        }),
        Animated.timing(fadeAnim, {
          toValue: 1,
          duration: 100,
          useNativeDriver: true,
        }),
      ]).start();
    }
  }, [user, fadeAnim]);

  const handleAuthAction = () => {
    const url = user ? '/home' : '/auth?view=login';
    router.push(url);
    if (isMobile) setIsMenuVisible(false);
  };

  const handleNavAndClose = (target) => {
    onNavPress(target);
    if (isMobile) setIsMenuVisible(false);
  };

  const handleCtaAndClose = () => {
    onCtaPress();
    if (isMobile) setIsMenuVisible(false);
  };

  const { logo, navLinks, cta } = content;
  const LogoComponent = logo.default || logo;

  // Filter nav links based on paywall flag
  const visibleNavLinks = navLinks.filter(
    (link) => !link.requiresPaywall || env.isPaywallEnabled
  );

  return (
    <>
      <View
        style={[
          styles.headerContainer,
          isMobile ? styles.mobileHeaderContainer : styles.desktopHeaderContainer,
        ]}
      >
        <TouchableOpacity
          style={[styles.logo, !isMobile && styles.desktopLogo]}
          onPress={() => router.push('/')}
          accessibilityRole="link"
          accessibilityLabel="Doocars home"
        >
          <LogoComponent width={100} height={28} accessibilityLabel="Doocars Logo" />
        </TouchableOpacity>

        {!isMobile && visibleNavLinks.length > 0 && (
          <View style={styles.navContainer} accessibilityRole="navigation">
            {visibleNavLinks.map((link) => (
              <TouchableOpacity
                key={link.title}
                onPress={() => onNavPress(link.target)}
                accessibilityRole="link"
              >
                <Text style={styles.navLink}>{link.title}</Text>
              </TouchableOpacity>
            ))}
          </View>
        )}

        {!isMobile && (
          <View style={[styles.buttonsContainer, styles.desktopButtons]}>
            <TouchableOpacity
              style={styles.authButton}
              onPress={handleAuthAction}
              accessibilityRole="button"
            >
              <Animated.Text style={[styles.authButtonText, { opacity: fadeAnim }]}>
                {user ? 'Dashboard' : 'Login'}
              </Animated.Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={styles.primaryCtaButton}
              onPress={onCtaPress}
              accessibilityRole="button"
            >
              <Text style={styles.primaryCtaButtonText}>{cta.title}</Text>
            </TouchableOpacity>
          </View>
        )}

        {isMobile && (
          <View style={styles.mobileRightSection}>
            <TouchableOpacity
              style={styles.mobileCtaButton}
              onPress={onCtaPress}
              accessibilityRole="button"
            >
              <Text style={styles.mobileCtaButtonText}>{cta.title}</Text>
            </TouchableOpacity>
            <TouchableOpacity onPress={() => setIsMenuVisible(true)}>
              <MenuIcon />
            </TouchableOpacity>
          </View>
        )}
      </View>

      {isMobile && (
        <Modal
          animationType="slide"
          transparent={false}
          visible={isMenuVisible}
          onRequestClose={() => setIsMenuVisible(false)}
        >
          <SafeAreaView style={styles.safeArea}>
            <View style={styles.modalContainer}>
              <TouchableOpacity style={styles.closeButton} onPress={() => setIsMenuVisible(false)}>
                <CloseIcon />
              </TouchableOpacity>
              <View style={styles.modalNavLinksContainer}>
                {visibleNavLinks.map((link) => (
                  <TouchableOpacity
                    key={link.title}
                    style={styles.modalNavLink}
                    onPress={() => handleNavAndClose(link.target)}
                  >
                    <Text style={styles.modalNavLinkText}>{link.title}</Text>
                  </TouchableOpacity>
                ))}
              </View>
              <View style={[styles.buttonsContainer, styles.mobileButtonsContainer]}>
                <TouchableOpacity
                  style={[styles.authButton, styles.mobileAuthButton]}
                  onPress={handleAuthAction}
                >
                  <Animated.Text style={[styles.authButtonText, styles.mobileAuthButtonText, { opacity: fadeAnim }]}>
                    {user ? 'Dashboard' : 'Login'}
                  </Animated.Text>
                </TouchableOpacity>
                <TouchableOpacity
                  style={[styles.primaryCtaButton, styles.mobilePrimaryCtaButton]}
                  onPress={handleCtaAndClose}
                >
                  <Text style={styles.primaryCtaButtonText}>{cta.title}</Text>
                </TouchableOpacity>
              </View>
            </View>
          </SafeAreaView>
        </Modal>
      )}
    </>
  );
};

const styles = StyleSheet.create({
  headerContainer: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    zIndex: 1000,
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    width: '100%',
    backgroundColor: 'rgba(10, 20, 40, 0.8)',
    backdropFilter: 'blur(10px)',
  },
  desktopHeaderContainer: {
    paddingHorizontal: 120,
    paddingVertical: 24,
  },
  mobileHeaderContainer: {
    paddingHorizontal: 24,
    paddingVertical: 16,
    backgroundColor: 'transparent',
    justifyContent: 'space-between',
  },
  mobileRightSection: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 16,
  },
  mobileCtaButton: {
    backgroundColor: '#1B60CA',
    borderRadius: 32,
    paddingVertical: 8,
    paddingHorizontal: 20,
  },
  mobileCtaButtonText: {
    fontFamily: fontFamilies.satoshi.bold,
    fontSize: 14,
    color: COLORS.white,
  },
  logo: {
    width: 110,
    height: 24,
  },
  desktopLogo: {
    position: 'absolute',
    left: 120,
  },
  navContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 48,
    paddingHorizontal: 32,
    paddingVertical: 16,
    borderRadius: 99,
    backgroundColor: 'rgba(255, 255, 255, 0.05)',
    backdropFilter: 'blur(12px)',
    borderWidth: 1,
    borderColor: 'rgba(255, 255, 255, 0.15)',
    boxShadow: 'inset 0px 0px 4px 0px rgba(0, 0, 0, 0.25)',
  },
  navLink: {
    fontFamily: fontFamilies.satoshi.regular,
    fontSize: 14,
    color: COLORS.white,
  },
  buttonsContainer: {
    flexDirection: 'row',
    gap: 16,
  },
  desktopButtons: {
    position: 'absolute',
    right: 120,
  },
  mobileButtonsContainer: {
    flexDirection: 'column',
  },
  primaryCtaButton: {
    borderRadius: 32,
    overflow: 'hidden',
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    paddingVertical: 12,
    paddingHorizontal: 40,
    alignItems: 'center',
    justifyContent: 'center',
  },
  mobilePrimaryCtaButton: {
    backgroundColor: '#1B60CA',
    paddingVertical: 16,
    alignItems: 'center',
  },
  primaryCtaButtonText: {
    fontFamily: fontFamilies.satoshi.bold,
    fontSize: 14,
    color: COLORS.white,
  },
  authButton: {
    backgroundColor: 'transparent',
    paddingVertical: 12,
    paddingHorizontal: 24,
    borderRadius: 32,
  },
  mobileAuthButton: {
    backgroundColor: 'rgba(27, 96, 202, 0.1)',
    paddingVertical: 16,
    alignItems: 'center',
  },
  authButtonText: {
    fontFamily: fontFamilies.satoshi.regular,
    fontSize: 14,
    color: COLORS.white,
  },
  mobileAuthButtonText: {
    color: '#1B60CA',
    fontSize: 16,
  },
  safeArea: {
    flex: 1,
    backgroundColor: COLORS.background,
  },
  modalContainer: {
    flex: 1,
    padding: 24,
    justifyContent: 'space-between',
  },
  closeButton: {
    alignSelf: 'flex-end',
    padding: 8,
  },
  modalNavLinksContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    gap: 16,
  },
  modalNavLink: {
    paddingVertical: 8,
  },
  modalNavLinkText: {
    fontFamily: fontFamilies.satoshi.bold,
    fontSize: 24,
    color: COLORS.textPrimary,
    textAlign: 'center',
  },
});

export default Header;
